"use client";

import { useTranslations } from "next-intl";
import { type Control, Controller, type FieldErrors } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type DynamicField,
  dynamicFieldName,
} from "@/lib/schemas/paycode/dynamic-field";

type DynamicFieldsValues = Record<string, string>;

interface DynamicFieldsProps {
  fields: DynamicField[];
  control: Control<DynamicFieldsValues>;
  errors: FieldErrors<DynamicFieldsValues>;
}

const inputModeFor = (type: DynamicField["type"]) =>
  type === "number" ? "numeric" : type === "email" ? "email" : "text";

const sanitizeFor = (type: DynamicField["type"], value: string) => {
  if (type === "number") return value.replace(/\D/g, "");
  if (type === "rfc" || type === "curp") return value.toUpperCase();
  return value;
};

export const DynamicFields = ({
  fields,
  control,
  errors,
}: DynamicFieldsProps) => {
  const t = useTranslations("common");
  if (fields.length === 0) return null;

  return (
    <>
      {fields.map((field) => {
        const name = dynamicFieldName(field.id);
        const error = errors[name];

        return (
          <div key={field.id} className="flex flex-col gap-1.5">
            <Label className="ml-2" htmlFor={name}>
              {field.name}
            </Label>
            <Controller
              control={control}
              name={name}
              render={({ field: rhfField }) =>
                field.type === "select" ? (
                  <Select
                    value={rhfField.value}
                    onValueChange={rhfField.onChange}
                  >
                    <SelectTrigger
                      id={name}
                      className="min-h-12 w-full rounded-lg"
                      aria-invalid={!!error}
                    >
                      <SelectValue
                        placeholder={t("selectPlaceholder", {
                          name: field.name,
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(field.options ?? []).map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={name}
                    placeholder={field.name}
                    inputMode={inputModeFor(field.type)}
                    maxLength={field.maxLength}
                    aria-invalid={!!error}
                    {...rhfField}
                    onChange={(e) =>
                      rhfField.onChange(
                        sanitizeFor(field.type, e.target.value).slice(
                          0,
                          field.maxLength,
                        ),
                      )
                    }
                  />
                )
              }
            />
            {error ? (
              <p className="ml-2 text-destructive text-sm">
                {error.message?.toString()}
              </p>
            ) : null}
          </div>
        );
      })}
    </>
  );
};
